import { ref, type Ref } from "vue";

/**
 * The engine runs a workflow's last *applied* (published) version, never the live draft — so Run with
 * unapplied edits silently runs stale content, and a never-published workflow runs nothing. This wraps
 * Run with an apply-first-if-dirty step: dirty opens a confirm, confirm publishes then runs the fresh
 * version; a publish-gate failure surfaces inline in the confirm and blocks the run.
 */
export function useApplyAndRun(deps: {
  hasDraftChanges: Ref<boolean>;
  // Un-forked subscriber base — publish would 403; edit must fork first. Not offered there.
  isBlockedFromApply: Ref<boolean>;
  applyAsync: () => Promise<unknown>;
  startRun: () => Promise<void>;
  resetApplyError: () => void;
}) {
  const confirmOpen = ref(false);
  const applying = ref(false);
  const running = ref(false);

  async function execute(): Promise<void> {
    if (running.value || applying.value) return;
    if (deps.hasDraftChanges.value && !deps.isBlockedFromApply.value) {
      deps.resetApplyError();
      confirmOpen.value = true;
      return;
    }
    await run();
  }

  async function confirm(): Promise<void> {
    if (applying.value) return;
    applying.value = true;
    try {
      await deps.applyAsync();
    } catch {
      // leave the confirm open; the error renders via the caller's applyError ref
      return;
    } finally {
      applying.value = false;
    }
    confirmOpen.value = false;
    await run();
  }

  async function run(): Promise<void> {
    running.value = true;
    try {
      await deps.startRun();
    } finally {
      running.value = false;
    }
  }

  function cancel(): void {
    if (applying.value) return;
    confirmOpen.value = false;
  }

  return { execute, confirm, cancel, confirmOpen, applying };
}
